"use client";

import Link from "next/link";
import { useFormStatus } from "react-dom";
import { Bookmark, CalendarCheck, Heart, Loader2 } from "lucide-react";
import { removeBookmark, removeEventCalendar, saveBookmark, saveEventCalendar, toggleLike } from "@/actions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import type { EventCalendarStatus, SavedEntityType, UserBookmark, UserCalendar } from "@/lib/supabase/types";
import { toDatetimeLocal } from "@/lib/utils";

type SavedEntityActionsProps = {
  entityType: SavedEntityType;
  entityId: string;
  entityName: string;
  redirectTo: string;
  isAuthenticated: boolean;
  bookmark?: UserBookmark | null;
  calendar?: UserCalendar | null;
  liked?: boolean;
};

function SubmitButton({ children }: { children: React.ReactNode }) {
  const { pending } = useFormStatus();
  return (
    <button type="submit" className="nv-button-primary min-h-10 justify-center" disabled={pending}>
      {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      {children}
    </button>
  );
}

export function SavedEntityActions({
  entityType,
  entityId,
  entityName,
  redirectTo,
  isAuthenticated,
  bookmark,
  calendar,
  liked
}: SavedEntityActionsProps) {
  const signupHref = `/auth/signup?redirectTo=${encodeURIComponent(redirectTo)}`;
  const isEvent = entityType === "event";
  const bookmarkLabel = bookmark ? "Edit bookmark" : "Bookmark";
  const status = (calendar?.status ?? "saved") as EventCalendarStatus;

  if (!isAuthenticated) {
    return (
      <div className="flex flex-wrap gap-3">
        <Link href={signupHref} className="nv-button-primary">
          <Bookmark className="h-4 w-4" />
          Bookmark
        </Link>
        <Link href={signupHref} className="nv-button-secondary">
          <Heart className="h-4 w-4" />
          Like
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-3">
      <Dialog>
        <DialogTrigger className="nv-button-primary" type="button">
          {isEvent ? <CalendarCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
          {bookmarkLabel}
        </DialogTrigger>
        <DialogContent className="p-6">
          <DialogHeader>
            <DialogTitle>{isEvent ? "Save event" : "Save bookmark"}</DialogTitle>
            <DialogDescription>
              {isEvent ? `Set your plan for ${entityName}.` : `Add ${entityName} to your bookmarks.`}
            </DialogDescription>
          </DialogHeader>
          <form action={isEvent ? saveEventCalendar : saveBookmark} className="grid gap-4">
            <input type="hidden" name="redirect_to" value={redirectTo} />
            {isEvent ? (
              <>
                <input type="hidden" name="event_id" value={entityId} />
                <label className="grid gap-1.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Attendance</span>
                  <select className="nv-input" name="status" defaultValue={status}>
                    <option value="saved">Saved</option>
                    <option value="going">Going</option>
                    <option value="maybe">Maybe</option>
                    <option value="not_going">Not going</option>
                  </select>
                </label>
                <label className="grid gap-1.5">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Reminder</span>
                  <input className="nv-input" name="reminder_at" type="datetime-local" defaultValue={toDatetimeLocal(calendar?.reminder_at)} />
                </label>
              </>
            ) : (
              <>
                <input type="hidden" name="entity_type" value={entityType} />
                <input type="hidden" name="entity_id" value={entityId} />
              </>
            )}
            <label className="grid gap-1.5">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Notes</span>
              <textarea className="nv-input min-h-24" name="notes" defaultValue={calendar?.notes ?? bookmark?.notes ?? ""} />
            </label>
            <DialogFooter>
              <SubmitButton>Save</SubmitButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {bookmark ? (
        <form action={removeBookmark}>
          <input type="hidden" name="entity_type" value={entityType} />
          <input type="hidden" name="entity_id" value={entityId} />
          <input type="hidden" name="redirect_to" value={redirectTo} />
          <button type="submit" className="nv-button-secondary">
            Remove bookmark
          </button>
        </form>
      ) : null}

      {isEvent && calendar ? (
        <form action={removeEventCalendar}>
          <input type="hidden" name="event_id" value={entityId} />
          <input type="hidden" name="redirect_to" value={redirectTo} />
          <button type="submit" className="nv-button-secondary">
            Remove calendar
          </button>
        </form>
      ) : null}

      <form action={toggleLike}>
        <input type="hidden" name="entity_type" value={entityType} />
        <input type="hidden" name="entity_id" value={entityId} />
        <input type="hidden" name="redirect_to" value={redirectTo} />
        <button type="submit" className="nv-button-secondary" aria-pressed={Boolean(liked)}>
          <Heart className={liked ? "h-4 w-4 fill-nv-red text-nv-red" : "h-4 w-4"} />
          {liked ? "Liked" : "Like"}
        </button>
      </form>
    </div>
  );
}
